import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  IncomingDocDto,
  Paginated,
  SurveyDto,
  SurveyWithLinesDto,
} from '@eombor/contracts';
import {
  accountingApi,
  type IncomingDocQuery,
  type IncomingDocRowDto,
  type SurveyQuery,
} from '../api/accounting.api.js';

/**
 * Buxgalteriya so'rovlari bitta joyda: kalitlar ham shu yerda turadi, shuning
 * uchun talabnoma o'tishidan keyin `invalidateQueries({ queryKey: accountingKeys.all })`
 * so'rovnoma navbatini ham, prixod ro'yxatini ham bir vaqtda yangilaydi.
 */
export const accountingKeys = {
  all: ['accounting'] as const,
  surveys: (query: SurveyQuery) => ['accounting', 'surveys', query] as const,
  docs: (query: IncomingDocQuery) => ['accounting', 'docs', query] as const,
  doc: (id: string | null) => ['accounting', 'doc', id] as const,
  survey: (id: string | null) => ['accounting', 'survey', id] as const,
};

/**
 * Buxgalter navbati o'zi yangilanadi.
 *
 * So'rovnoma navbatga BOSHQA xodimning amalidan tushadi (skladchi talabnomani
 * qabul qiladi) — sahifa ochiq turganda hech qanday mahalliy hodisa
 * bo'lmaydi. 60 soniya: "mening navbatim" bilan bir xil oraliq.
 */
export const SURVEY_REFETCH_MS = 60_000;

export const useSurveys = (query: SurveyQuery = {}): UseQueryResult<Paginated<SurveyDto>> =>
  useQuery({
    queryKey: accountingKeys.surveys(query),
    queryFn: () => accountingApi.listSurveys(query),
    refetchInterval: SURVEY_REFETCH_MS,
  });

export const useIncomingDocs = (
  query: IncomingDocQuery = {},
): UseQueryResult<Paginated<IncomingDocRowDto>> =>
  useQuery({
    queryKey: accountingKeys.docs(query),
    queryFn: () => accountingApi.listDocs(query),
  });

/** Bitta prixod — SATRLARI bilan; ro'yxatdagi yozuvda ular yo'q. */
export const useIncomingDoc = (id: string | null): UseQueryResult<IncomingDocDto> =>
  useQuery({
    queryKey: accountingKeys.doc(id),
    queryFn: () => accountingApi.doc(id ?? ''),
    enabled: id !== null,
  });

/**
 * Bitta so'rovnoma — SATRLARI bilan.
 *
 * Ro'yxat javobida satrlar yo'q va bo'lmasligi ham kerak: ular faqat
 * hujjatni ochganda kerak bo'ladi va har ro'yxat so'rovida yuzlab satrni
 * olib kelish bekorga trafik bo'lardi.
 */
export const useSurvey = (id: string | null): UseQueryResult<SurveyWithLinesDto> =>
  useQuery({
    queryKey: accountingKeys.survey(id),
    queryFn: () => accountingApi.survey(id ?? ''),
    enabled: id !== null,
  });

/**
 * Blankdagi «ТРЕБОВАНИЕ №» ni yozish.
 *
 * Javobdagi hujjat darhol keshga yoziladi: raqam saqlangandan keyin blank
 * QAYTA so'raladi va u yangi raqam bilan chizilishi kerak. Blank so'rovi
 * ham bekor qilinadi, aks holda ekranda eski PDF qolib ketardi.
 */
export const useSetSurveyFormNo = (
  id: string,
): UseMutationResult<SurveyWithLinesDto, unknown, string | null> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (formNo: string | null) => accountingApi.setSurveyFormNo(id, formNo),
    onSuccess: (survey) => {
      client.setQueryData(accountingKeys.survey(id), survey);
      void client.invalidateQueries({ queryKey: accountingKeys.all });
      void client.invalidateQueries({ queryKey: ['accounting', 'requirement'], exact: false });
    },
  });
};
